import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';

import crvSystemsMark from '../assets/branding/crv-systems-mark.svg';
import { crvWebTokens } from '../theme';

const shellTopBarHeight = `${String(crvWebTokens.shell.topBarHeight)}px`;

export interface ShellNavItem {
  to: string;
  label: string;
  icon?: ReactNode;
  end?: boolean;
  activeWhen?: readonly string[];
  inactiveWhen?: readonly string[];
}

export interface ShellNavGroup {
  id: string;
  label: string;
  items: readonly ShellNavItem[];
}

interface AppShellProps {
  workspaceLabel: string;
  homePath: string;
  navGroups: readonly ShellNavGroup[];
  workspaceSwitcher: ReactNode;
  accountEmail?: string | null;
  companyLinkLabel?: string | null;
  companyLinkTo?: string;
  showBillingLink?: boolean;
  billingBanner?: ReactNode;
  onLogout: () => void;
}

export function AppShell({
  workspaceLabel,
  homePath,
  navGroups,
  workspaceSwitcher,
  accountEmail,
  companyLinkLabel,
  companyLinkTo = '/select-company',
  showBillingLink = false,
  billingBanner,
  onLogout,
}: AppShellProps): JSX.Element {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const closeMobileNav = (): void => {
    setMobileNavOpen(false);
  };

  const navigation = (
    <ShellNavigation
      navGroups={navGroups}
      onNavigate={closeMobileNav}
    />
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        color: 'text.primary',
        minWidth: 0,
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        color="default"
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
        }}
      >
        <Toolbar
          sx={{
            minHeight: shellTopBarHeight,
            px: { xs: 1.5, sm: 2.5, lg: 3 },
            gap: 1.5,
          }}
        >
          {isCompact && (
            <IconButton
              edge="start"
              aria-label="Open navigation"
              onClick={() => {
                setMobileNavOpen(true);
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <BrandLink homePath={homePath} workspaceLabel={workspaceLabel} />

          <Box sx={{ flex: 1 }} />

          {!isCompact && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              {companyLinkLabel && (
                <Button
                  component={RouterLink}
                  to={companyLinkTo}
                  color="inherit"
                  variant="outlined"
                  size="small"
                  sx={{ maxWidth: 240 }}
                >
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {companyLinkLabel}
                  </Box>
                </Button>
              )}
              {showBillingLink && (
                <Button component={RouterLink} to="/billing" color="inherit" size="small">
                  Billing
                </Button>
              )}
              {workspaceSwitcher}
              {accountEmail && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    maxWidth: 220,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {accountEmail}
                </Typography>
              )}
              <Button variant="outlined" size="small" onClick={onLogout}>
                Logout
              </Button>
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
        {!isCompact && (
          <Box
            component="aside"
            sx={{
              width: { sm: 244, lg: crvWebTokens.shell.railWidth },
              flex: '0 0 auto',
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              minHeight: `calc(100vh - ${shellTopBarHeight})`,
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                top: shellTopBarHeight,
                maxHeight: `calc(100vh - ${shellTopBarHeight})`,
                overflowY: 'auto',
                p: 2,
              }}
            >
              {navigation}
            </Box>
          </Box>
        )}

        <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
          {billingBanner}
          <Container
            maxWidth={false}
            sx={{
              width: '100%',
              maxWidth: '100%',
              px: { xs: 2, sm: 2.5, lg: 3 },
              py: { xs: 2, sm: 3, lg: 4 },
              minWidth: 0,
            }}
          >
            <Outlet />
          </Container>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={isCompact && mobileNavOpen}
        onClose={closeMobileNav}
        ModalProps={{ keepMounted: true }}
        slotProps={{
          paper: {
            sx: {
              width: 'min(88vw, 340px)',
              bgcolor: 'background.paper',
            },
          },
        }}
      >
        <Stack spacing={2} sx={{ minHeight: '100%', p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <BrandLink homePath={homePath} workspaceLabel={workspaceLabel} onClick={closeMobileNav} />
            <IconButton aria-label="Close navigation" onClick={closeMobileNav}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider />
          {navigation}
          <Divider />

          <Stack spacing={1}>
            {companyLinkLabel && (
              <Button
                component={RouterLink}
                to={companyLinkTo}
                variant="outlined"
                color="inherit"
                onClick={closeMobileNav}
                fullWidth
              >
                {companyLinkLabel}
              </Button>
            )}
            {showBillingLink && (
              <Button
                component={RouterLink}
                to="/billing"
                variant="outlined"
                color="inherit"
                onClick={closeMobileNav}
                fullWidth
              >
                Billing
              </Button>
            )}
            <Box>{workspaceSwitcher}</Box>
            {accountEmail && (
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                {accountEmail}
              </Typography>
            )}
            <Button variant="outlined" onClick={onLogout} fullWidth>
              Logout
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </Box>
  );
}

function BrandLink({
  homePath,
  workspaceLabel,
  onClick,
}: {
  homePath: string;
  workspaceLabel: string;
  onClick?: () => void;
}): JSX.Element {
  return (
    <Box
      component={RouterLink}
      to={homePath}
      onClick={onClick}
      aria-label="CRV Systems home"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 0,
        color: 'inherit',
        textDecoration: 'none',
      }}
    >
      <Box
        component="img"
        src={crvSystemsMark}
        alt=""
        sx={{ width: 36, height: 36, flex: '0 0 auto', borderRadius: 1 }}
      />
      <Stack spacing={0} sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
          CRV Systems
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1.2, whiteSpace: 'nowrap' }}
        >
          {workspaceLabel}
        </Typography>
      </Stack>
    </Box>
  );
}

function ShellNavigation({
  navGroups,
  onNavigate,
}: {
  navGroups: readonly ShellNavGroup[];
  onNavigate: () => void;
}): JSX.Element {
  const location = useLocation();
  const visibleGroups = navGroups.filter((group) => group.items.length > 0);

  return (
    <Stack component="nav" aria-label="Main navigation" spacing={2}>
      {visibleGroups.map((group) => {
        const groupActive = group.items.some((item) => isNavItemActive(item, location.pathname));
        return (
          <Box
            key={group.id}
            role="group"
            aria-label={groupActive ? `${group.label}, current section` : group.label}
            data-active={groupActive ? 'true' : 'false'}
          >
            <Typography
              variant="overline"
              color={groupActive ? 'primary' : 'text.secondary'}
              sx={{
                display: 'block',
                px: 1,
                pb: 0.75,
                fontWeight: 800,
                letterSpacing: 0,
                lineHeight: 1.4,
              }}
            >
              {group.label}
            </Typography>
            <Stack spacing={0.5}>
              {group.items.map((item) => {
                const active = isNavItemActive(item, location.pathname);
                return (
                  <ListItemButton
                    key={item.to}
                    component={RouterLink}
                    to={item.to}
                    selected={active}
                    aria-current={active ? 'page' : undefined}
                    onClick={onNavigate}
                    sx={{
                      minHeight: 40,
                      borderRadius: 1,
                      color: active ? 'primary.dark' : 'text.primary',
                      '&.Mui-selected': {
                        bgcolor: crvWebTokens.brand.green100,
                        color: 'primary.dark',
                        fontWeight: 800,
                        '&:hover': {
                          bgcolor: crvWebTokens.brand.green100,
                        },
                      },
                    }}
                  >
                    {item.icon && (
                      <ListItemIcon
                        sx={{
                          minWidth: 34,
                          color: active ? 'primary.main' : 'text.secondary',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={item.label}
                      slotProps={{
                        primary: {
                          variant: 'body2',
                          fontWeight: active ? 800 : 650,
                          noWrap: true,
                        },
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

function isNavItemActive(item: ShellNavItem, pathname: string): boolean {
  if (
    item.inactiveWhen?.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
  ) {
    return false;
  }

  if (item.end || item.to === '/') {
    return pathname === item.to;
  }

  if (item.activeWhen?.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return true;
  }

  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}
